import { useParams, Link } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';

interface PrRecord {
  verdict?: string;
  tier?: number;
  score?: number;
  rationale?: string;
  artifacts?: Record<string, unknown>[];
  reviews?: Record<string, unknown>[];
  incidents?: Record<string, unknown>[];
  [key: string]: unknown;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border rounded-lg p-4 mb-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function KVTable({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => !Array.isArray(v) && typeof v !== 'object');
  if (entries.length === 0) return <p className="text-sm text-gray-400">—</p>;
  return (
    <table className="text-sm w-full">
      <tbody className="divide-y divide-gray-100">
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td className="py-1 pr-4 text-gray-500 font-medium w-40">{k}</td>
            <td className="py-1 font-mono text-xs break-all">{String(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ArraySection({ items }: { items: Record<string, unknown>[] }) {
  if (!items || items.length === 0) return <p className="text-sm text-gray-400">None.</p>;
  const keys = [...new Set(items.flatMap(i => Object.keys(i)))];
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left">
          <tr>
            {keys.map(k => (
              <th key={k} className="px-3 py-1.5 font-medium text-gray-600 text-xs">{k}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((item, i) => (
            <tr key={i} className="hover:bg-gray-50">
              {keys.map(k => (
                <td key={k} className="px-3 py-1.5 font-mono text-xs">
                  {item[k] === undefined ? '' : typeof item[k] === 'object' ? JSON.stringify(item[k]) : String(item[k])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PrDetail() {
  const { org, repo, pr } = useParams<{ org: string; repo: string; pr: string }>();
  const path = `/api/pr/${org}/${repo}/${pr}`;
  const { data, isLoading, error } = useApiQuery<PrRecord>(['pr', org, repo, pr], path);

  const topLevel = data
    ? Object.fromEntries(
        Object.entries(data).filter(([, v]) => !Array.isArray(v) && typeof v !== 'object' || v === null)
      )
    : {};

  return (
    <div>
      <div className="mb-4">
        <Link to={`/repos/${org}/${repo}`} className="text-sm text-blue-600 hover:underline">
          ← {org}/{repo}
        </Link>
      </div>

      <h2 className="text-lg font-semibold mb-4">
        PR #{pr} — {org}/{repo}
      </h2>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">Error: {String(error)}</p>}

      {data && (
        <>
          <Section title="Verdict">
            <KVTable data={topLevel as Record<string, unknown>} />
          </Section>

          <Section title="Artifacts">
            <ArraySection items={(data.artifacts ?? []) as Record<string, unknown>[]} />
          </Section>

          <Section title="Reviews">
            <ArraySection items={(data.reviews ?? []) as Record<string, unknown>[]} />
          </Section>

          <Section title="Incidents">
            <ArraySection items={(data.incidents ?? []) as Record<string, unknown>[]} />
          </Section>
        </>
      )}
    </div>
  );
}
